import 'react-day-picker/dist/style.css';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import 'reactjs-popup/dist/index.css';
import { COOKIES_CONFIG, SYSTEM_MAINTENANCE_NOTICE } from '@belong/common';
// Webpack is not finding @belong/ui/index.css for some reason, works for the rest of the exports
import { MaintenanceNoticeToast } from '@belong/ui';
import '@belong/ui/dist/index.css';
import { datadogRum } from '@datadog/browser-rum';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import '@reach/accordion/styles.css';
import '@reach/dialog/styles.css';
import '@reach/slider/styles.css';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { AnalyticsProvider } from 'analytics';
import { setEnhancedConvertionData, pageview } from 'analytics/ga.utils';
import { ApiClientProvider } from 'common/contexts/api-client';
import ReduxAsyncConnect from 'components/ReduxAsyncConnect/ReduxAsyncConnect';
import { ConnectedRouter } from 'connected-react-router';
import 'core-js/stable';
import { isEmpty } from 'es-toolkit/compat';
import { GoogleAnalytics } from 'helpers/GoogleAnalytics';
import { GoogleTagManager } from 'helpers/GoogleTagManager';
import apiClient from 'helpers/apiClient';
import buildOctaneClient from 'helpers/buildOctaneClient';
import { initDataDog } from 'helpers/datadog';
import { startMaintenanceHeartbeat } from 'helpers/maintenance';
import { RedditPixel } from 'helpers/reddit-pixel';
import 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import { parseCookies } from 'nookies';
import App from 'pages/App/App';
import { combineReducers } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import 'regenerator-runtime/runtime';
import 'resize-observer-polyfill';
import createStore from 'store/redux/create';
import createReducers from 'store/redux/reducers';
import { fetchUser } from 'store/redux/user/actions';
import theme from 'styles/theme';
import { ThemeProvider } from 'theme-ui';
import {
  applyGlobalPrivacyControl,
  applyStoredConsentModeDefaults,
  hasConsent,
} from 'utils/consent';
import { createHistory } from 'utils/history';
import uuidv1 from 'uuid/v1';
import './index.css';
import { trackSiteVisit } from './store/redux/common/uploadDocumentToS3';
import {
  selectExperiments,
  updateExperiments,
} from './store/redux/experiments';
import { HOMEOWNER_ONBARDING_LEADRS_REDUCER } from './store/redux/homeowner-onboarding/leads-reducer';
import { selectUser } from './store/redux/user/selectors';
import CustomerioAnalyticsScript from './utils/CustomerioAnalyticsScript';

console.log('Build Number:', __BUILD_NUMBER__);

initDataDog();

if (process.env.NEXT_PUBLIC_MAINTENANCE_MODE === '1') {
  window.location.replace(`${process.env.NEXT_PUBLIC_HOST}/maintenance`);
}

const { JWT_TOKEN } = COOKIES_CONFIG;

const env = process.env.NEXT_PUBLIC_ENV;
const mixpanelApiKey = process.env.NEXT_PUBLIC_MIXPANEL_API_KEY;
const accessibilityPreferences = {
  themePreference: window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light',
  languagePreference: window.navigator.language,
  reducedMotionPreference: window.matchMedia('(prefers-reduced-motion: reduce)')
    .matches,
  isHighContrastModePreference: window.matchMedia('(forced-colors: active)')
    .matches,
};
applyGlobalPrivacyControl();

// Must precede the GTM container, which `helpers/GoogleTagManager.js` injects
// from an effect once React mounts.
applyStoredConsentModeDefaults();

if (window?.mixpanel && mixpanelApiKey) {
  const isDevMode = ['dev1', 'dev2', 'dev3', 'staging'].includes(env);
  const ENABLE_SESSION_RECORDING = false;
  const shouldRecord = env === 'production' || ENABLE_SESSION_RECORDING;

  const mixpanelConfig = {
    debug: isDevMode,
    track_pageview: true,
    persistence: 'localStorage',
    record_mask_text_selector: '', // Don't mask text.
    record_mask_text_class: 'input[type="password"]',
    record_block_selector: '', // Unmask all images/videos
    record_sessions_percent: shouldRecord ? 100 : 0,
  };

  // The Mixpanel loader snippet in `src/index.html` runs unconditionally and
  // cannot be gated from here, so consent is enforced through Mixpanel's own
  // opt-in/opt-out API instead — the same mechanism c15t drives in belong-next.
  const hasMeasurementConsent = hasConsent('measurement');

  mixpanelConfig.opt_out_tracking_by_default = !hasMeasurementConsent;

  if (!hasMeasurementConsent) {
    // Session replay is the highest-risk capture here and must not start
    // before consent, regardless of environment.
    mixpanelConfig.record_sessions_percent = 0;
  }

  window.mixpanel?.init(mixpanelApiKey, mixpanelConfig);

  if (hasMeasurementConsent) {
    window.mixpanel?.opt_in_tracking();
  } else {
    window.mixpanel?.opt_out_tracking();
  }

  window.mixpanel?.register({
    'First Visited URL': window.location.href,
    'First Visited Path': window.location.pathname,
    ...accessibilityPreferences,
  });
}

const client = apiClient();
const octaneClient = buildOctaneClient();
const cookies = parseCookies();
const jwtToken = cookies[JWT_TOKEN.name];

const history = createHistory();

let error = console.error;
console.error = function (warning) {
  if (/Failed %s type/.test(warning)) {
    // alert(warning);
    return;
  }

  error.apply(console, arguments);
};

const trackingIdCookieConfig = COOKIES_CONFIG.TRACKING_ID;
const trackingId = cookies[trackingIdCookieConfig.name] || uuidv1();

const providers = {
  client,
  octaneClient,
};

(async () => {
  // Catch maintenance that starts while the SPA is already open. Runs an
  // immediate check on load, then polls belong-next's maintenance-windows
  // route — detection is independent of how any screen fetches its data.
  startMaintenanceHeartbeat();

  const persistConfig = {
    key: 'rootPersist',
    storage,
    whitelist: [HOMEOWNER_ONBARDING_LEADRS_REDUCER],
  };

  const reducers = createReducers(history);
  const combinedReducers = combineReducers({
    ...reducers,
  });

  const rootReducer = persistReducer(persistConfig, combinedReducers);

  // Needed when redirecting from the next project to a place where we need to pass address
  // and keep user session even if passwordless.
  const preloadedHomeownerOnboardingLeads = {};
  try {
    const addressFromNext = localStorage.getItem('address-from-next');

    if (addressFromNext) {
      const { coordinates, ...parsedAddress } = JSON.parse(addressFromNext);

      preloadedHomeownerOnboardingLeads.addresses = [
        { ...parsedAddress, ...coordinates },
      ];

      localStorage.removeItem('address-from-next');
    }
  } catch (e) {
    console.log(e);
  }

  const prelodadedData = {
    homeownerOnboardingLeads: preloadedHomeownerOnboardingLeads,
  };

  const store = createStore({
    rootReducer,
    history,
    helpers: providers,
    data: prelodadedData,
  });

  persistStore(store);

  const { dispatch, getState } = store;

  if (jwtToken) {
    client.setJwtToken(jwtToken);
    octaneClient.setJwtToken(jwtToken);

    await dispatch(fetchUser());
  }

  const partnerKey = cookies[COOKIES_CONFIG.PARTNER_KEY.name];
  client.setTrackingId(trackingId);
  octaneClient.setTrackingId(trackingId);
  trackSiteVisit(trackingId, jwtToken, partnerKey).catch((err) =>
    console.error(err),
  );

  let experimentsFromCookie = null;
  try {
    const experimentsConfig = COOKIES_CONFIG.EXPERIMENTS;
    const experimentsString = cookies[experimentsConfig.name];
    const experimentsParsed = experimentsString
      ? JSON.parse(experimentsString)
      : undefined;
    if (!isEmpty(experimentsParsed)) {
      experimentsFromCookie = experimentsParsed;
    }
  } catch (experimentsError) {
    console.log(experimentsError);
  }

  const experiments = experimentsFromCookie || selectExperiments(getState());
  dispatch(updateExperiments(experiments));
  pageview(window.location.pathname + window.location.search);

  const currentUser = selectUser(getState());
  if (currentUser?.userId) {
    window.mixpanel?.identify?.(currentUser.userId);
    window.mixpanel?.register?.({
      email: currentUser.email,
    });

    datadogRum.setUser({
      id: currentUser.userId,
      email: currentUser.email,
    });

    setEnhancedConvertionData(currentUser);
  }

  const sdkConfig = {
    core: {
      authorizationKey: process.env.NEXT_PUBLIC_SPLITIO_APIKEY,
      key: currentUser?.userId || 'anon',
    },
  };

  const container = document.getElementById('root');
  const root = createRoot(container);
  root.render(
    <SplitFactoryProvider config={sdkConfig}>
      <CustomerioAnalyticsScript />
      <Provider store={store} {...providers}>
        <AnalyticsProvider>
          <TooltipProvider delayDuration={50} skipDelayDuration={150}>
            <ConnectedRouter history={history}>
              <ApiClientProvider apiClient={client}>
                <ReduxAsyncConnect>
                  <ThemeProvider theme={theme}>
                    <App>
                      <GoogleAnalytics />
                      <GoogleTagManager />
                      <RedditPixel />
                      <MaintenanceNoticeToast {...SYSTEM_MAINTENANCE_NOTICE} />
                    </App>
                  </ThemeProvider>
                </ReduxAsyncConnect>
              </ApiClientProvider>
            </ConnectedRouter>
          </TooltipProvider>
        </AnalyticsProvider>
      </Provider>
    </SplitFactoryProvider>,
  );
})();
